import { MapPin, Search, X } from "lucide-react"
import { useCallback, useRef, useState } from "react"
import { AutocompleteInput } from "@/components/forms/autoCompleteInput"
import { formatLocation } from "@/components/shared/formatLocation"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"

interface IntentionSearchBarProps {
  onSearchChange: (query: string) => void
  onLocationChange?: (city?: string, state?: string, country?: string) => void
  className?: string
}

export function IntentionSearchBar({
  onSearchChange,
  onLocationChange,
  className = "",
}: IntentionSearchBarProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const user = useAuthStore(state => state.user)
  const { t } = useTranslation("learnPractice")
  const defaultLocation = formatLocation(user?.city, user?.state)
  const [locationDisplay, setLocationDisplay] = useState(defaultLocation ?? "Santa Monica")

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value
      setSearchQuery(query)
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => onSearchChange(query), 300)
    },
    [onSearchChange],
  )

  const handleLocationSelect = useCallback(
    (loc: { city?: string; state?: string; country?: string }) => {
      setLocationDisplay(
        loc.city
          ? `${loc.city}${loc.state ? `, ${loc.state}` : ""}`
          : loc.state
            ? `${loc.state}${loc.country ? `, ${loc.country}` : ""}`
            : (loc.country ?? ""),
      )
      onLocationChange?.(loc.city, loc.state, loc.country)
    },
    [onLocationChange],
  )

  const handleLocationClear = useCallback(() => {
    setLocationDisplay(defaultLocation ?? "Santa Monica")
    onLocationChange?.(undefined, undefined, undefined)
  }, [defaultLocation, onLocationChange])

  return (
    <div className={`mt-0 pt-0 ${className}`}>
      <div className="relative">
        <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-[25px] h-[25px] text-primary opacity-90" />
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder={t("search.placeholder")}
          className="w-full h-[65px] pl-[49px] pr-5 md:pr-52 rounded-[15px] bg-[rgba(242,240,235,0.05)] border border-[rgba(242,240,235,0.15)] text-primary placeholder:text-primary/90 text-sm leading-[25px] focus:outline-none focus:ring-1 focus:ring-brand-olive"
        />
        <div className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 items-center gap-1.25 text-primary text-sm font-semibold whitespace-nowrap opacity-90">
          <MapPin className="w-[25px] h-[25px]" />
          <AutocompleteInput
            label=""
            mode="full"
            placeholder={t("search.location")}
            value={locationDisplay}
            onLocationSelect={handleLocationSelect}
            className="h-auto min-h-0 p-0 border-0 text-primary text-sm font-semibold bg-transparent"
          />
          {locationDisplay !== (defaultLocation ?? "Santa Monica") && (
            <button
              type="button"
              onClick={handleLocationClear}
              className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-primary/20 transition-colors"
              aria-label={t("search.clearLocation")}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
